<template>
	<Col class="width-100 height-100 flex-justify-space-between form-container">
		<Col>
			<div class="name mt-3">
				<p><label>Inbox name</label></p>
				<AppTextField class="input mt-3" autofocus outlined dense v-model="inboxName" required hide-details single-line />
			</div>
			<h3 class="medium mt-6">Choose icon</h3>
			<IconStyleEditor class="mb-2 mt-n5" :btnSettings.sync="icon" :allowColor="false" />
			<AppLoader v-if="loadingInbox" />
			<template v-else>
				<div class="mt-3 mb-6">
					<h3 class="medium">Add users and groups</h3>
					<small>Who will have access to this inbox</small>
					<UserSelectCombobox
						class="mt-2"
						:userIdsToExclude="userIdsToExclude"
						:groupIdsToExclude="groupIdsToExclude"
						:comboboxSelection.sync="comboboxSelection"
					/>
				</div>

				<div class="mt-3 mb-6">
					<p><label>Send in</label></p>
					<div v-for="{ config, channel } in channelItems" :key="channel.id" class="pl-4">
						<div class="d-flex flex-row">
							<SettingSwitch :isSwitchDisabled="channel.is_forced && config.is_on" :title="channel.name" :switchValue.sync="config.is_on" />
						</div>
						<div class="pl-4" v-if="config.is_on">
							<p class="warning-message" v-if="shouldShowNotice(channel)">
								<IconWarning class="warning-icon" />
								For this to work, a workspace admin needs to configure a connection in <a @click="openSettings">workspace settings</a>.
							</p>
							<template v-else>
								<ParamDispatcher
									v-for="param in getVisibleActionParams(channel.service_action)"
									:node="{ params_map: config.channel_params, params_map_labels: config.channel_params_labels }"
									:nodeService="getService(channel.service)"
									:nodeAction="getAction(channel.service_action)"
									:param="{ ...param, disable_mapped_data: true, channelId: channel.id }"
									:key="param.key"
									hasEditPermissions
									@update-input-field="({ map: { key, value } = {} }) => $set(config.channel_params, key, value)"
									@update-input-field-label="({ label: { key, value } = {} }) => $set(config.channel_params_labels, key, value)"
								/>
							</template>
						</div>
					</div>
				</div>
			</template>
		</Col>
		<div class="flex-center--end my-4">
			<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId">
				<template #default="{ indicator, isUpgradeRequired }">
					<AppTooltip :disabled="canEdit" position="top" text="You don't have permission to edit this inbox">
						<PrimaryButton
							:loading="isLoading"
							@click="(e) => submit(e, isUpgradeRequired, indicator)"
							:disabled="!canEdit || isSubmitDisabled || loadingInbox"
						>
							{{ submitText }}
							<component v-if="isUpgradeRequired" :is="indicator" class="ml-2 mt-1" :billingFeatureId="billingFeatureId" iconOnly />
						</PrimaryButton>
					</AppTooltip>
				</template>
			</MaybeUpgradeRequiredIndicator>
		</div>
	</Col>
</template>

<script>
import { mapGetters } from 'vuex';

import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import ParamDispatcher from '@/components/BlockEditor/BlockEditorParams/ParamDispatcher.vue';
import IconStyleEditor from '@/components/BlockEditor/ButtonEditorDrawer/tabs/IconStyleEditor.vue';
import SettingSwitch from '@/components/BlockEditorWidget/FieldSettings/WidgetSettings/SettingSwitch';
import IconWarning from '@/components/FlowTreeBlock/assets/icon-exclamation-unknown.svg';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import UserSelectCombobox from '@/components/dialogs/ShareModuleDialog/UserSelectCombobox';
import { teamMemberByUserId, credByServiceVersionId } from '@/store/modules/account/getters';
import { getGroups } from '@/store/modules/accountGroups/actions';
import { groupById, groups } from '@/store/modules/accountGroups/getters';
import { getAction, getActionParams } from '@/store/modules/actions/getters';
import { getServiceData, getActionsOfApp } from '@/store/modules/apps/actions';
import { getService } from '@/store/modules/apps/getters';
import { createInbox } from '@/store/modules/inboxes/actions';
import { fetchChannels } from '@/store/modules/notifications/actions';
import { channelsMapping, channels } from '@/store/modules/notifications/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';

export default {
	name: 'InboxForm',
	components: {
		IconWarning,
		SettingSwitch,
		ParamDispatcher,
		IconStyleEditor,
		UserSelectCombobox,
		MaybeUpgradeRequiredIndicator,
	},
	props: {
		canEdit: { type: Boolean },
		isLoading: { type: Boolean },
		editInboxName: { type: String },
		editInboxChannelConfigs: { type: Array },
		submitText: { type: String, required: true },
		editInboxUsers: { type: Array, default: () => [] },
		editInboxGroups: { type: Array, default: () => [] },
		editInboxIconName: { type: String, default: 'ply-element-transparent' },
	},

	computed: {
		...mapGetters(['user']),
		...mapGettersShorthand({
			groups,
			channels,
			groupById,
			getAction,
			getService,
			channelsMapping,
			getActionParams,
			teamMemberByUserId,
			credByServiceVersionId,
		}),
		billingFeatureId() {
			return FeatureTypes.SharedNotificationInboxes;
		},
		userIdsToExclude() {
			return [];
		},
		groupIdsToExclude() {
			return (this.groups || []).filter((group) => group.usage_preferences?.send_notification !== true).map(({ id }) => id);
		},
		channelItems() {
			return (this.localChannelConfigs || []).map((config) => ({ config, channel: this.channelsMapping[config.channel] }));
		},
		isSubmitDisabled() {
			const onChannels = this.localChannelConfigs.filter(({ is_on }) => is_on);
			for (const { channel_params, channel: channelId } of onChannels) {
				const channel = this.channelsMapping[channelId];
				const visibleRequiredParams = this.getVisibleActionParams(channel.service_action).filter(({ required }) => required);
				for (const { key } of visibleRequiredParams) {
					if (!channel_params[key]) {
						return true;
					}
				}
			}
			return !this.inboxName;
		},
	},
	methods: {
		...mapActionsShorthand({ getServiceData, getActionsOfApp, fetchChannels, createInbox, getGroups }),
		async submit(event, isUpgradeRequired, indicator) {
			if (isUpgradeRequired && indicator?.setup) {
				indicator.setup().openUpgrade(event, this.billingFeatureId);
			} else {
				const fields = {
					name: this.inboxName,
					icon_name: this.icon.icon.name,
					users: this.comboboxSelection.filter(({ isUser }) => isUser).map(({ user_id }) => user_id),
					groups: this.comboboxSelection.filter(({ isGroup }) => isGroup).map(({ id }) => id),
					channel_configs: this.localChannelConfigs,
				};
				this.$emit('submit', fields);
			}
		},
		buildDefaultChannelConfig(channelId) {
			return { channel: channelId, is_on: true, channel_params: {}, channel_params_labels: {} };
		},
		getVisibleActionParams(action) {
			return this.getActionParams(action).filter(({ hidden }) => !hidden);
		},
		shouldShowNotice(channel) {
			return channel?.requires_cred && !this.credByServiceVersionId(channel.service_version_id);
		},
		openSettings() {
			AppDialogManagerService.openSettingsDialog({ routeName: 'SettingsOrganization' });
		},
	},
	created() {
		this.icon.icon.name = this.editInboxIconName;
		if (this.editInboxName) {
			this.inboxName = this.editInboxName;
		}
	},
	async mounted() {
		this.loadingInbox = true;
		await Promise.all([this.fetchChannels(), this.getGroups()]);

		this.comboboxSelection.push(...this.editInboxUsers.map(this.teamMemberByUserId), ...this.editInboxGroups.map(this.groupById));

		if (this.editInboxChannelConfigs) {
			this.localChannelConfigs = this.editInboxChannelConfigs.map((x) => ({ ...x }));
		}

		this.channels
			.filter(({ is_inbox }) => is_inbox)
			.forEach((channel) => {
				this.getServiceData({ id: channel.service });
				this.getActionsOfApp({ id: channel.service, version: channel.service_version });
				if (!this.localChannelConfigs.find((localChannelConfig) => localChannelConfig.channel === channel.id)) {
					this.localChannelConfigs.push(this.buildDefaultChannelConfig(channel.id));
				}
			});

		this.localChannelConfigs.sort((a, b) => (this.channelsMapping[b.channel].is_forced ? 1 : -1));

		this.loadingInbox = false;
	},
	data() {
		return {
			inboxName: '',
			loadingInbox: false,
			comboboxSelection: [],
			localChannelConfigs: [],
			icon: {
				icon: {
					size: 12,
					name: 'ply-element-transparent',
				},
			},
		};
	},
};
</script>

<style scoped lang="scss">
small {
	color: gray;
}
.primary-button {
	min-width: 215px !important;
}
::v-deep {
	.app-combobox .v-select__slot .v-select__selections {
		padding-right: 5px !important;
	}
	.user-select-combobox .app-combobox .v-input > .v-input__control > .v-input__slot {
		max-height: none;
	}
	.user-select-combobox .app-combobox .v-select__selections {
		position: static;
	}
}
.form-container {
	min-height: 585px;
}
</style>
